import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import Button from '../../shared/components/Button';
import ClubbieModal from '../../components/Modal/ClubbieModal';
import DatePicker from '../../components/DatePicker/DatePicker';
import Input from '../../shared/components/Input';
import StepsTracker from '../../components/StepsTracker/StepsTracker';
import uploadIcon from '../../assets/images/plus.png';
import UserIcon from '../../assets/images/user.png';
import { editProfile, fetchStockAvatars, uploadProfileImage } from '../../redux/actions/authActions';
import clubbieLogo from '../../assets/images/clubbie_logo_full.svg';
import { logoutUser } from '../../redux/slices/authSlice';
import { AUTH_STACK } from '../../utils/screenNames';

/**
 * PersonalDetails Page - Refactored with Tailwind CSS
 * Step 1 of signup details: profile image, name, gender, DOB, minor info
 */
function PersonalDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const user = useSelector((state) => state.user);
  const profile = user?.user?.profile;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('Male');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [isMinor, setIsMinor] = useState(false);
  const [parentsName, setParentsName] = useState('');
  const [parentsEmail, setParentsEmail] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null); // { id, uri } from stock API
  const [profileImageUrl, setProfileImageUrl] = useState(''); // final URL to display & save
  const [isStock, setIsStock] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Prefill from existing profile data (e.g. when navigating back from LocationDetails)
  useEffect(() => {
    if (!profile) return;
    if (profile.firstName) setFirstName(profile.firstName);
    if (profile.secondName) setLastName(profile.secondName);
    if (profile.image) setProfileImageUrl(profile.image);
    if (profile.gender) {
      const g = profile.gender.toLowerCase();
      setGender(g === 'male' ? 'Male' : g === 'female' ? 'Female' : 'Other');
    }
    if (profile.dateOfBirth) {
      const d = new Date(profile.dateOfBirth);
      if (!Number.isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        setDateOfBirth(`${yyyy}-${mm}-${dd}`);
        setIsMinor(isUserUnder13(`${yyyy}-${mm}-${dd}`));
      }
    }
  }, [profile]);

  // Stock avatars from API
  const [stockAvatars, setStockAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  const loadStockAvatars = useCallback(async () => {
    if (stockAvatars.length > 0) return; // already loaded
    try {
      setLoadingAvatars(true);
      const avatars = await fetchStockAvatars();
      setStockAvatars(avatars);
    } catch {
      // silently fail — user can still upload
    } finally {
      setLoadingAvatars(false);
    }
  }, [stockAvatars.length]);

  /**
   * Utils
   */
  const isUserUnder13 = (dob) => {
    if (!dob) return false;
    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return false;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }

    return age < 13;
  };

  const openModal = () => {
    setIsModalOpen(true);
    loadStockAvatars();
  };
  const closeModal = () => setIsModalOpen(false);

  const handleSaveAvatar = () => {
    if (selectedAvatar) {
      // Stock image — use its URI directly
      setProfileImageUrl(selectedAvatar.uri);
      setIsStock(true);
    } else if (selectedFile && preview) {
      // Custom upload — store preview for display, file will be uploaded on Continue
      setProfileImageUrl(preview);
      setIsStock(false);
    }
    closeModal();
  };

  const handleCancelAvatar = () => {
    setSelectedAvatar(null);
    setSelectedFile(null);
    setPreview(null);
    setValidationError(null);
    closeModal();
  };

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    setSelectedFile(null);
    setPreview(null);
    setValidationError(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setValidationError(null);

    if (!file) {
      setValidationError('Please select a file to upload.');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setValidationError('Invalid file type. Please upload a SVG, PNG, JPG, or GIF image.');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setValidationError('File size too large. Maximum size is 5MB.');
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      setSelectedFile(file);
      setPreview(img.src);
      setSelectedAvatar(null);
    };
    img.onerror = () => {
      setValidationError('Invalid image file. Please try again.');
    };
  };

  const handleDateChange = (value) => {
    setDateOfBirth(value);
    const under13 = isUserUnder13(value);
    setIsMinor(under13);
  };

  /**
   * Handle Continue
   */
  const handleContinue = async () => {
    setSubmitError('');

    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError('Please provide both first and last name.');
      return;
    }

    if (!dateOfBirth) {
      setSubmitError('Please select your date of birth.');
      return;
    }

    // Prevent impossible dates being sent to the backend (e.g. future DOB)
    const parsedDob = new Date(dateOfBirth);
    const today = new Date();

    if (!Number.isNaN(parsedDob.getTime()) && parsedDob > today) {
      setSubmitError('Date of birth cannot be in the future.');
      return;
    }

    if (isMinor && (!parentsName.trim() || !parentsEmail.trim())) {
      setSubmitError('Please provide parent / guardian name and email.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Resolve image URL — upload custom file or use stock URI
      let imageUrl = '';
      if (profileImageUrl && !isStock && selectedFile) {
        imageUrl = await uploadProfileImage(selectedFile);
      } else if (profileImageUrl && isStock) {
        imageUrl = profileImageUrl;
      }

      const personalData = {
        fullName: `${firstName} ${lastName}`.trim(),
        firstName: firstName.trim(),
        secondName: lastName.trim(),
        gender: gender === 'Other' ? 'unknown' : gender ? gender.toLowerCase() : '',
        dateOfBirth,
        ...(imageUrl && { image: imageUrl }),
        ...(isMinor && {
          isMinor: true,
          guardianName: parentsName.trim(),
          guardianEmail: parentsEmail.trim(),
        }),
      };

      setIsSubmitting(false);
      navigate(AUTH_STACK.LOCATION_DETAILS, {
        state: {
          from: AUTH_STACK.PERSONAL_DETAILS,
          email: location?.state?.email,
          locationData: location?.state?.locationData,
          personalData,
        },
      });
    } catch (error) {
      setIsSubmitting(false);
      setSubmitError('Failed to upload image. Please try again.');
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser())
      .unwrap()
      .then(() => {
        navigate(AUTH_STACK.LOGIN_OPTIONS, { replace: true });
      })
      .catch(() => {
        navigate(AUTH_STACK.LOGIN_OPTIONS, { replace: true });
      });
  };

  const renderAvatarImage = () => {
    if (!profileImageUrl) return <img src={UserIcon} alt="User" className="w-8 h-8" />;
    return <img src={profileImageUrl} alt="Profile" className="w-full h-full rounded-full object-cover" />;
  };

  return (
    <div className="relative w-screen h-screen bg-bg-dark flex flex-col items-center justify-center">
      {/* Logo - Top Right */}
      <div className="absolute top-4 right-6 z-10">
        <img className="h-auto w-[120px]" src={clubbieLogo} alt="clubbie logo" />
      </div>

      {/* Main Content - Centered */}
      <div className="w-full max-w-xl px-6 mx-auto border-[0.5px] border-app-border py-8 rounded-2xl max-h-[calc(100vh-80px)] overflow-y-auto [&::-webkit-scrollbar]:hidden">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-semibold text-[#FEFCEB] mb-1 font-proxima">Create your account</h1>
          <p className="text-[13px] text-app-text-secondary font-light font-proxima">Enter your details to create your account.</p>
        </div>

        {/* Steps Tracker */}
        <div className="mb-5">
          <StepsTracker currentStep={1} />
        </div>

        {/* Form Container */}
        <div className="w-full px-4">
          {/* Image Section */}
          <div className="mb-4">
            <label className="block text-[14px] font-normal text-app-text mb-1.5 ">Image</label>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-[120px] h-[120px] border border-dashed border-[#3a4453] rounded-full flex items-center justify-center overflow-hidden bg-transparent">
                  {renderAvatarImage()}
                </div>
                <button
                  type="button"
                  onClick={openModal}
                  className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[#06203A] flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <img src={uploadIcon} alt="Upload" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[14px] font-normal text-app-text mb-1.5 ">First Name</label>
              <Input
                type="text"
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[14px] font-normal text-app-text mb-1.5 ">Last Name</label>
              <Input
                type="text"
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          {/* Gender */}
          <div className="mb-4">
            <label className="block text-[14px] font-normal text-app-text mb-1.5 ">Gender</label>
            <div className="flex flex-col gap-2">
              {['Male', 'Female', 'Other'].map((option) => (
                <label
                  key={option}
                  className="relative inline-flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="gender"
                    value={option}
                    checked={gender === option}
                    onChange={(e) => setGender(e.target.value)}
                    className="sr-only peer"
                  />
                  <div className="w-4 h-4 rounded-full border-[1.5px] border-[#333E48] peer-checked:border-blue-shade-2 flex items-center justify-center transition-colors">
                    {gender === option && (
                      <div className="w-2 h-2 rounded-full bg-blue-shade-2"></div>
                    )}
                  </div>
                  <span className="text-sm text-[#FEFCEB]">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Date of Birth */}
          <div className="mb-4">
            <label className="block text-[14px] font-normal text-app-text mb-1.5 ">Date of birth</label>
            <DatePicker
              value={dateOfBirth}
              onChange={handleDateChange}
              disablePastDates={false}
            />
          </div>

          {/* Minor Warning & Parent Details */}
          {isMinor && (
            <div className="mb-3">
              <p className="text-xs text-shaded-text mb-3 leading-relaxed">
                <span className="text-gray-200 font-medium">Parental consent required. </span>
                If you're under 13, a parent or guardian must approve your use of Clubbie.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-[14px] font-normal text-app-text mb-1.5 ">
                    Parent / guardian name
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter name"
                    value={parentsName}
                    onChange={(e) => setParentsName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-[14px] font-normal text-app-text mb-1.5 ">
                    Parent / guardian email address
                  </label>
                  <Input
                    type="email"
                    placeholder="Enter email"
                    value={parentsEmail}
                    onChange={(e) => setParentsEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <p className="text-sm text-red-500 mb-3">{submitError}</p>
          )}

          {/* Continue Button */}
          <Button
            className="flex-1 w-full h-11 bg-[#00E7FF] mt-5 mb-5 text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={handleContinue}
            disabled={
              isSubmitting ||
              !firstName.trim() ||
              !lastName.trim() ||
              !gender ||
              !dateOfBirth ||
              (isMinor && (!parentsName.trim() || !parentsEmail.trim()))
            }
          >
            Continue
          </Button>

          {/* Log out */}
          <button
            type="button"
            onClick={() => setShowLogoutModal(true)}
            className="w-full text-center text-sm text-app-text-secondary hover:opacity-50 transition-colors"
          >
            Log out
          </button>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ClubbieModal
        isOpen={showLogoutModal}
        toggle={() => setShowLogoutModal(false)}
        title="Log out"
        width="400px"
        maxHeight="auto"
        footer={
          <>
            <button
              type="button"
              onClick={() => setShowLogoutModal(false)}
              className="px-6 py-2.5 bg-app-card rounded-full text-app-text hover:bg-[#1e3a57] transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="px-6 py-2.5 rounded-full bg-[#00E7FF] text-[#021529] text-sm font-medium transition-colors hover:bg-[#00d4eb]"
            >
              Log out
            </button>
          </>
        }
      >
        <p className="text-sm text-app-text-secondary">
          Are you sure you want to log out?
        </p>
      </ClubbieModal>

      {/* Avatar Modal */}
      <ClubbieModal
        isOpen={isModalOpen}
        toggle={closeModal}
        title="Add Profile Picture"
        minHeight="auto"
        maxHeight="85vh"
        footer={
          <>
            <button
              type="button"
              onClick={handleCancelAvatar}
              className="px-6 py-2.5 bg-app-card rounded-full text-app-text hover:bg-[#1e3a57] transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAvatar}
              disabled={!selectedAvatar && !selectedFile}
              className="px-6 py-2.5 rounded-full bg-[#00E7FF] text-[#021529] text-sm font-medium transition-colors hover:bg-[#00d4eb] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirm
            </button>
          </>
        }
      >
        <div className="text-[#FEFCEB] px-3 py-3 max-w-md mx-auto">
          {/* Preview Circle */}
          <div className="flex justify-center mb-5">
            <div className="w-28 h-28 rounded-full border border-blue-shade-7 flex items-center justify-center overflow-hidden bg-blue-shade-9">
              {selectedAvatar ? (
                <img src={selectedAvatar.uri} alt="Selected avatar" className="w-full h-full object-cover" />
              ) : preview ? (
                <img src={preview} alt="Uploaded preview" className="w-full h-full object-cover" />
              ) : (
                <img src={UserIcon} alt="User" className="w-10 h-10 opacity-30" />
              )}
            </div>
          </div>

          {/* Upload Button */}
          <div className="mb-4">
            <label
              htmlFor="fileInput"
              className="flex items-center justify-center gap-2 w-full h-10 rounded-full bg-[#082441] text-[#FEFCEB] text-sm font-proxima cursor-pointer hover:bg-[#0a2f57] transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10m9.333-5.333L8 1.333m0 0L4.667 4.667M8 1.333v9.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Upload new photo
            </label>
            <input
              id="fileInput"
              type="file"
              accept=".svg,.png,.jpg,.jpeg,.gif"
              onChange={handleFileUpload}
              className="hidden"
            />
            {validationError && (
              <p className="mt-2 text-xs text-red-500 text-center">{validationError}</p>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center my-4 text-xs text-shaded-text">
            <span className="flex-1 h-px bg-blue-shade-8" />
            <span className="px-3">or</span>
            <span className="flex-1 h-px bg-blue-shade-8" />
          </div>

          {/* Gallery Title */}
          <p className="text-xs text-shaded-text mb-3">Choose from the gallery</p>

          {/* Avatar Grid */}
          {loadingAvatars ? (
            <div className="text-center text-sm text-app-text-secondary py-4">Loading avatars...</div>
          ) : (
            <div className="grid grid-cols-3 gap-8 px-6">
              {stockAvatars.map((avatar) => (
                <button
                  key={avatar.id}
                  type="button"
                  onClick={() => handleAvatarSelect(avatar)}
                  className="relative w-full aspect-square transition-all hover:scale-105"
                >
                  <div
                    className="w-full h-full rounded-full overflow-hidden border-2 bg-blue-shade-9"
                    style={{ borderColor: selectedAvatar?.id === avatar.id ? '#00E7FF' : 'transparent' }}
                  >
                    <img src={avatar.uri} alt="Avatar" className="w-full h-full object-cover" />
                  </div>
                  {selectedAvatar?.id === avatar.id && (
                    <div className="absolute bottom-1 right-1 w-5 h-5 bg-[#00E7FF] rounded-full flex items-center justify-center">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#081725" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </ClubbieModal>
    </div>
  );
}

export default PersonalDetails;
